/**
 * Runtime configuration loader.
 *
 * The console fetches /config.json at boot and caches the result in
 * memory for the lifetime of the page. This eliminates the build-time
 * `VITE_SUPABASE_*` bake pattern: one Docker image now serves every
 * environment, and Coolify (or whatever orchestrator) supplies the
 * env-specific values via container env vars that an entrypoint
 * splices into the served `/config.json`.
 *
 * In dev (Vite), `public/config.json` provides a static fallback so
 * `npm run dev` keeps working without a backend.
 *
 * Call `loadRuntimeConfig()` once at app boot before rendering any
 * component that touches Supabase. Hooks/services then call
 * `getRuntimeConfig()` synchronously inside effects.
 */

export interface RuntimeConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
}

let _cached: RuntimeConfig | null = null;
let _loadPromise: Promise<RuntimeConfig> | null = null;

/**
 * Fetch /config.json once and cache. Idempotent: subsequent calls
 * return the cached config (or the in-flight promise on race).
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (_cached) return _cached;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    let res: Response;
    try {
      res = await fetch('/config.json', { cache: 'no-store' });
    } catch (err) {
      _loadPromise = null;
      throw new Error(
        `/config.json fetch threw: ${err instanceof Error ? err.message : String(err)}`
      );
    }

    if (!res.ok) {
      _loadPromise = null;
      throw new Error(`/config.json fetch failed: ${res.status}`);
    }

    let json: unknown;
    try {
      json = await res.json();
    } catch (err) {
      _loadPromise = null;
      throw new Error(
        `/config.json parse failed: ${err instanceof Error ? err.message : String(err)}`
      );
    }

    if (!isRuntimeConfig(json)) {
      _loadPromise = null;
      throw new Error('/config.json missing supabaseUrl or supabaseAnonKey');
    }

    _cached = json;
    return _cached;
  })();

  return _loadPromise;
}

/**
 * Synchronous accessor — throws if `loadRuntimeConfig()` hasn't
 * resolved yet. Use inside effects or after-render code paths where
 * the boot-time load has already completed.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!_cached) {
    throw new Error('Runtime config not loaded. Call loadRuntimeConfig() first.');
  }
  return _cached;
}

/**
 * Test-only escape hatch: reset the cached config so a fresh
 * `loadRuntimeConfig()` will re-fetch. Production code must not call
 * this — use only from unit tests.
 */
export function __resetRuntimeConfigForTests(): void {
  _cached = null;
  _loadPromise = null;
}

function isRuntimeConfig(value: unknown): value is RuntimeConfig {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.supabaseUrl === 'string' &&
    obj.supabaseUrl.length > 0 &&
    typeof obj.supabaseAnonKey === 'string' &&
    obj.supabaseAnonKey.length > 0
  );
}
